import React, { useState, useEffect } from 'react';
import { Cloud, CloudOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import cloudSync from '../utils/cloudSync';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * CloudSyncIndicator - Shows cloud sync status for premium users
 * Displays sync status and allows manual sync
 */
const CloudSyncIndicator = ({ compact = false }) => {
  const { user } = useAuth();
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const isPremium = user?.subscription_status === 'active';

  useEffect(() => {
    if (isPremium) {
      fetchSyncStatus();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/cloud/user-sync-status');
      setSyncStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await cloudSync.batchSyncToCloud();
      
      if (result.isPreviewLimitation) {
        toast.info('Cloud sync is unavailable in preview environment. It will work after deployment.', {
          duration: 5000
        });
      } else if (result.synced > 0) {
        toast.success(`Synced ${result.synced} receipts to cloud`);
        fetchSyncStatus();
      } else if (result.already_synced > 0 && result.failed === 0) {
        toast.info('All receipts are already synced!');
      } else if (result.failed > 0 && result.synced === 0) {
        // All failed - likely R2 connection issue in preview
        toast.warning('Cloud sync failed - R2 connection unavailable in preview environment. Will work after deployment.', {
          duration: 5000
        });
      } else {
        toast.info('No receipts to sync');
      }
    } catch (err) {
      // Connection errors in preview environment
      toast.warning('Cloud sync unavailable in preview. Will work after deployment.', {
        duration: 4000
      });
    } finally {
      setSyncing(false);
    }
  };

  // Don't show for non-premium users
  if (!isPremium) return null;

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (!syncStatus?.cloud_enabled) {
    return (
      <div className="flex items-center space-x-2 text-yellow-400" data-testid="cloud-not-configured">
        <CloudOff className="w-4 h-4" />
        <span className="text-sm">Cloud not configured</span>
      </div>
    );
  }

  // Compact version for navbar/header
  if (compact) {
    return (
      <div 
        className="flex items-center space-x-2 cursor-pointer group"
        onClick={handleSync}
        data-testid="cloud-sync-indicator-compact"
        title={`${syncStatus.synced_to_cloud}/${syncStatus.receipts_with_images} synced`}
      >
        {syncStatus.pending_sync > 0 ? (
          <>
            <Cloud className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-yellow-400">{syncStatus.pending_sync} pending</span>
          </>
        ) : (
          <>
            <Cloud className="w-4 h-4 text-green-400" />
            <CheckCircle className="w-3 h-3 text-green-400 -ml-1" />
          </>
        )}
        {syncing && <RefreshCw className="w-3 h-3 animate-spin text-indigo-400" />}
      </div>
    );
  }

  // Full version for dashboard/settings
  return (
    <div 
      className="p-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl"
      data-testid="cloud-sync-indicator"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Cloud className="w-5 h-5 text-indigo-400" />
          <span className="font-medium">Cloud Backup</span>
        </div>
        {syncStatus.pending_sync === 0 ? (
          <span className="flex items-center space-x-1 text-green-400 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>All synced</span>
          </span>
        ) : (
          <span className="flex items-center space-x-1 text-yellow-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{syncStatus.pending_sync} pending</span>
          </span>
        )}
      </div>
      
      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>{syncStatus.synced_to_cloud} of {syncStatus.receipts_with_images} receipts synced</span>
          <span>{syncStatus.sync_percentage}%</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
            style={{ width: `${syncStatus.sync_percentage}%` }}
          />
        </div>
      </div>
      
      {syncStatus.pending_sync > 0 && (
        <button
          onClick={handleSync}
          disabled={syncing}
          data-testid="sync-now-btn"
          className="w-full py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 rounded-lg flex items-center justify-center space-x-2 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'Syncing...' : 'Sync Now'}</span>
        </button>
      )}
    </div>
  );
};

export default CloudSyncIndicator;
